import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useMiniERP } from './hooks/useMiniERP';
import { useOfflineCounterMemo } from './hooks/useOfflineSync';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faWifi, faCloudUploadAlt, faCheckCircle, faExclamationTriangle,
  faSpinner, faSync, faSignal
} from '@fortawesome/free-solid-svg-icons';

// Deliberately the simplest possible version of Quick Counter Memo —
// Customer-only (no Business/Barter, no voice — SpeechRecognition itself
// needs network to reach Google's servers, so voice can't work offline
// anyway), built for low-end Android / patchy-signal use.
const MiniERPAI = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const userId = authState?.user?._id;
  const { createPettyTransaction } = useMiniERP();
  const { status, showSuccess, showError } = useStatusBanner();

  const {
    browserOnline, pending, pendingCount, failedCount, syncing,
    submitEntry, syncQueue, isSupported,
  } = useOfflineCounterMemo({ createPettyTransaction, userId });

  const [form, setForm] = useState({
    type: 'Sales', itemName: '', amount: '', qty: '', unit: '',
    paymentType: 'Cash', isPaid: true, customerName: '', customerMobile: '', remarks: '',
  });

  const resetForm = () => setForm({
    type: 'Sales', itemName: '', amount: '', qty: '', unit: '',
    paymentType: 'Cash', isPaid: true, customerName: '', customerMobile: '', remarks: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.itemName || !form.amount) {
      showError('Item name and amount are required.');
      return;
    }
    const payload = {
      ...form,
      amount: Number(form.amount),
      qty: form.qty ? Number(form.qty) : undefined,
      unit: form.qty ? form.unit : undefined,
      date: new Date(),
    };
    try {
      const result = await submitEntry(payload);
      if (result.queued) {
        showSuccess('📴 No connection right now — saved on this device. Will sync automatically once you\'re back online.');
      } else {
        showSuccess('✅ Saved and synced.');
      }
      resetForm();
    } catch (err) {
      showError(err.message);
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: { icon: faSignal, color: 'text-amber-400', label: 'Waiting for signal' },
      syncing: { icon: faSpinner, color: 'text-cyan-400', label: 'Syncing...', spin: true },
      synced: { icon: faCheckCircle, color: 'text-emerald-400', label: 'Synced' },
      failed: { icon: faExclamationTriangle, color: 'text-rose-400', label: 'Failed' },
    };
    const m = map[s] || map.pending;
    return (
      <span className={`flex items-center gap-1 text-xs ${m.color}`}>
        <FontAwesomeIcon icon={m.icon} spin={!!m.spin} /> {m.label}
      </span>
    );
  };

  if (!isSupported) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-6 text-center">
        <p className="text-rose-400 mt-10">This device's browser doesn't support offline storage — please use the regular Home page instead.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4">
      <div className="max-w-lg mx-auto">
        {/* Connection status */}
        <div className={`flex items-center justify-between rounded-xl px-4 py-2 mb-4 text-sm ${
          browserOnline ? 'bg-emerald-950/40 border border-emerald-700 text-emerald-300' : 'bg-amber-950/40 border border-amber-700 text-amber-300'
        }`}>
          <span className="flex items-center gap-2">
            <FontAwesomeIcon icon={faWifi} />
            {browserOnline ? 'Online' : 'No signal — entries save on this device'}
          </span>
          {pendingCount > 0 && (
            <button
              type="button"
              onClick={syncQueue}
              disabled={syncing}
              className="w-fit px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faSync} spin={syncing} /> {pendingCount} waiting — Sync Now
            </button>
          )}
        </div>

        <h1 className="text-xl font-bold text-cyan-200 mb-1">Offline Counter Entry</h1>
        <p className="text-xs text-gray-500 mb-4">Sales &amp; Purchase only — works even with no signal. Voice isn't available here since it needs a connection.</p>

        <StatusBanner status={status} className="mb-4" />

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-2xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm">
                <option value="Sales">Sales</option>
                <option value="Purchase">Purchase</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Payment Mode</label>
              <select value={form.paymentType} onChange={(e) => setForm({ ...form, paymentType: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm">
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
                <option value="Credit">Credit</option>
                <option value="Free">Free</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-xs block mb-1">Item Name</label>
            <input type="text" value={form.itemName} onChange={(e) => setForm({ ...form, itemName: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <input type="number" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
            <input type="number" placeholder="Qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
            <input type="text" placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" disabled={!form.qty} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Customer / Supplier" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
            <input type="tel" placeholder="Mobile" value={form.customerMobile} onChange={(e) => setForm({ ...form, customerMobile: e.target.value.replace(/[^\d+ ]/g, '') })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
          </div>

          <textarea placeholder="Remarks (optional)" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            style={{ height: '70px' }} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />

          <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-xl py-3 font-medium flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faPlus} /> Save {form.type}
          </button>
        </form>

        {pending.length > 0 && (
          <div className="mt-5">
            <h2 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faCloudUploadAlt} /> On This Device ({pending.length})
              {failedCount > 0 && <span className="text-rose-400 text-xs">({failedCount} failed)</span>}
            </h2>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {pending.map((row) => (
                <div key={row.clientId} className="bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 flex justify-between items-center text-xs">
                  <div>
                    <div className="text-white font-medium">{row.payload.itemName}</div>
                    <div className="text-gray-500">₹{row.payload.amount} • {new Date(row.createdAt).toLocaleString()}</div>
                  </div>
                  {statusBadge(row.syncStatus)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniERPAI;